import React, { useState, useRef, useEffect } from "react";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Col from "react-bootstrap/Col";
import BarcodeScanner from "../barcode/BarcodeScanner";
import { FaQrcode } from "react-icons/fa";
import ReactModal from "react-modal";
import { LookUpFormField } from "./LookUpFormField";
import useTaskDetailsFormControlStore from '../stores/TaskDetailsFormControlStore';
import useTaskStore from '../stores/useTaskStore';
import { formatSealWatermeterId } from "../app/WmUtils";
import { toast } from "react-toastify";

// Mirror of the #222 downgrade-on-clear in onInputChange: since #377 a new row starts at
// "Hiányzik" (null) when its identifier is empty, so entering the identifier afterwards
// upgrades it to "Ép". Only on the actual empty -> filled transition (so a "Hiányzik" the
// installer deliberately picks on an already-filled row is never overridden on a later
// blur), and only from "Hiányzik" - "Sérült" is installer-entered data and is never touched.
// okDomValue is the uncontrolled <select>'s DOM value ("Hiányzik" | "true" | "false").
export const shouldAutoSetOkOnIdentifierEntry = (prevIdentifier, newIdentifier, okDomValue) =>
  !prevIdentifier && !!newIdentifier && newIdentifier !== "---" && okDomValue === "Hiányzik";

const WmeterForm = (props) => {
  const [propState, setPropState] = useState(props);
  const [barCodeRes, setBarCodeRes] = useState(null);
  const [barcodeScannerOn, setBarcodeScannerOn] = useState(false);
  const [lookupFieldReload, setLookupFieldReload] = useState(null);
  const [localPhysicalPlace, setLocalPhysicalPlace] = useState(propState.defaultPhysicalPlace || "");
  const cold_hotRef = useRef();
  const placeRef = useRef();
  const sizeRef = useRef();
  const lengthRef = useRef();
  const transmitterRef = useRef();  
  const meterValidFromRef = useRef();  
  const modelRef = useRef();
  const okRef = useRef();
  const meterRef = useRef();
  const idRef = useRef();
  const p1idRef = useRef();
  const p1okRef = useRef();
  const p2idRef = useRef();
  const p2okRef = useRef();
  const noteRef = useRef();

  const {isButtonDisabled} = useTaskDetailsFormControlStore((state) => ({
    isButtonDisabled: state.isButtonDisabled
}));
  const { selectedTask } = useTaskStore();

  const propStateListWmItem = props.wmWorkList[`${props.actTask}`].wmlist[props.item];
  propStateListWmItem.current=props.wmWorkList[`${props.actTask}`].wmlist[props.item];
  const isNewWaterMeter = !!propStateListWmItem.current?.is_new;

  // Durable "was this plain-text identifier field genuinely cleared" tracking for the
  // isNewWaterMeter===false (Leszerelt) tab's plain <Form.Control> fields (GitLab #373
  // review, Critical #A). Unlike the Select variant - where LookUpFormField.jsx's onChange
  // writes a durable "---" sentinel directly into the ref, so meterIdCleared/seal1IdCleared
  // can just read it back - a plain <input>'s own DOM value can't durably encode "cleared"
  // (it's just "" either way), and the wmlist item's MODEL value can't be used as that
  // durable signal either: this same onInputChange call is what nulls the model out the
  // moment the clear commits, so "was the model set before this call" reads true on exactly
  // that one blur and false on every blur after - the guard protecting the
  // wm_meter_id_or_meter_not_ok / wm_plomba1_id_or_plomba1_not_ok DB constraints would stop
  // firing the instant the installer picked "Ép" on ANY later, unrelated blur, silently
  // reintroducing the exact violation GitLab #222 fixed.
  //
  // These refs instead persist independently of the model, driven purely by comparing the
  // field's own raw DOM value against a snapshot of what it was as of the last time
  // onInputChange ran (updatePlainTextClearedTracking below) - initialized from the item's
  // actual starting identifier so a genuine first clear is detected even if it's the very
  // first field interaction in the whole form (no other field needs to blur first to "prime"
  // the baseline). Reset to false by any real (non-empty) value, whether typed or written
  // directly by a barcode scan - a scan writes the ref immediately (barCodeRes effect below)
  // but the "cleared" flag itself only clears on the next onInputChange/blur that reads it,
  // same as a typed value - mirroring how LookUpFormField.jsx's justClearedRef gets reset
  // by a real pick there.
  const meterIdLastRawValueRef = useRef(propStateListWmItem.current?.meter_identifier ?? "");
  const meterIdClearedRef = useRef(false);
  const seal1IdLastRawValueRef = useRef(propStateListWmItem.current?.plomba1_identifier ?? "");
  const seal1IdClearedRef = useRef(false);
  const seal2IdLastRawValueRef = useRef(propStateListWmItem.current?.plomba2_identifier ?? "");
  const seal2IdClearedRef = useRef(false);

  const updatePlainTextClearedTracking = (lastRawValueRef, clearedRef, currentRawValue) => {
    if (currentRawValue === "" && !!lastRawValueRef.current) {
      // First detection of a genuine clear: the field held a real value as of the last time
      // this ran, and now it doesn't.
      clearedRef.current = true;
    } else if (currentRawValue !== "") {
      // A real value is present again (typed, or written directly by a barcode scan) - no
      // longer "cleared".
      clearedRef.current = false;
    }
    // else: currentRawValue === "" and it already was "" (or unset) last time - leave
    // clearedRef exactly as it is, so a genuine clear detected on an earlier blur stays
    // durably recognized across every later, unrelated blur.
    lastRawValueRef.current = currentRawValue;
  };

  useEffect(() => {
    setLocalPhysicalPlace(propState.defaultPhysicalPlace);
  },[propState.defaultPhysicalPlace]);

  useEffect(() => {
    const primaryWatermeterLocation  = !!propStateListWmItem.current.watermeter_physical_place
    ? propStateListWmItem.current.watermeter_physical_place
    : selectedTask.work_watermeter_physical_place;
    setLocalPhysicalPlace(primaryWatermeterLocation );
  },[]);

  useEffect(() => {
    setPropState(props);
  }, [props]);

  useEffect(() => {
      propStateListWmItem.current = propState.wmWorkList[`${propState.actTask}`].wmlist[propState.item];
  }, [propState]);

  const updateWaterMeterData = (identifier) => {
    let wmItem=propState.wmList?.current.find((element) => element.watermeter_id == identifier);
    if(!!wmItem){
      cold_hotRef.current.value=wmItem.is_cold ?? undefined;
      modelRef.current.value=wmItem.meter_model;
      lengthRef.current.value=wmItem.meter_length_in_mm;
      meterValidFromRef.current.value=wmItem.meter_valid_from;
      transmitterRef.current.value=wmItem.meter_is_transmitter_supported ?? undefined;
      toast.success("Vízmérő adatok frissítve a gyáriszám alapján.");
    } else {
      //default values not here defined 
      return;
    }
  };

  /*eslint-disable eqeqeq*/
  const onInputChange = async (event) => {
    event?.preventDefault();
    let tempInvItem = Object.assign({}, propState.wmWorkList);
    const actInvItemListItem =
      tempInvItem[`${propState.actTask}`].wmlist[propState.item];
    actInvItemListItem.is_cold = cold_hotRef.current.value;
    actInvItemListItem.watermeter_physical_place = placeRef.current.value;
    actInvItemListItem.size = sizeRef.current.value;
    actInvItemListItem.meter_ok = okRef.current.value;
    actInvItemListItem.act_meter = meterRef.current.value;
    actInvItemListItem.meter_length_in_mm = lengthRef.current.value;
    actInvItemListItem.meter_is_transmitter_supported = transmitterRef.current.value;
    actInvItemListItem.meter_valid_from = meterValidFromRef.current.value;
    actInvItemListItem.meter_model = modelRef.current.value;
    // "---" is the shared "intentionally empty" sentinel LookUpFormField.jsx's onChange
    // writes to these refs when the user clears a Select field (see GitLab #222) - it has
    // to be a real, non-empty string so the `!!ref.current.value` gate below still lets the
    // clear through, and is then mapped to a real `null` here, exactly as already done for
    // plomba2_identifier's "üres" option.
    //
    // When isNewWaterMeter is false, wmID/seal1ID/seal2ID render as plain <Form.Control>
    // text inputs instead (see the isNewWaterMeter ? <LookUpFormField/> : <Form.Control/>
    // branches below) - those refs point at a real, uncontrolled <input>, whose .value is
    // always a determinate string once mounted: "" both when the installer has cleared it
    // AND when the field simply started empty and was never touched at all (an "" DOM value
    // cannot tell those two apart on its own - unlike the Select variant's "---", which is
    // never a value that arises except through an explicit clear/"üres" pick). Neither can a
    // plain "" check be upgraded by comparing against the wmlist item's MODEL value (an
    // earlier version of this fix tried exactly that, via a `!!actInvItemListItem.<field>`
    // "was it set before" check) - this same call is what nulls the model out the moment the
    // clear commits, so that comparison reads true on exactly the one blur that commits the
    // clear and false on every blur after, silently un-recognizing the field as cleared one
    // blur later. See meterIdClearedRef (declared above, updated via
    // updatePlainTextClearedTracking) for the durable, model-independent replacement.
    if (!isNewWaterMeter) {
      updatePlainTextClearedTracking(meterIdLastRawValueRef, meterIdClearedRef, idRef?.current?.value ?? "");
    }
    const meterIdCleared = isNewWaterMeter
      ? idRef?.current?.value == "---"
      : meterIdClearedRef.current;
    const prevMeterIdentifier = actInvItemListItem.meter_identifier;
    if (!!idRef?.current?.value || meterIdCleared) {
      actInvItemListItem.meter_identifier = meterIdCleared ? null : idRef.current.value;
    }
    if (shouldAutoSetOkOnIdentifierEntry(prevMeterIdentifier, actInvItemListItem.meter_identifier, okRef?.current?.value)) {
      // DOM select corrected too, same reason as the downgrade below: the next blur re-reads it.
      actInvItemListItem.meter_ok = "true";
      okRef.current.value = "true";
      toast.info("A gyáriszám megadásával a vízmérő állapota 'Ép'-re állt.");
    }
    if (meterIdCleared && okRef?.current?.value == "true") {
      // DB check constraint wm_meter_id_or_meter_not_ok requires a non-empty
      // meter_identifier only when meter_ok is true ("Ép") - meter_ok=false ("Sérült") with
      // no identifier is already constraint-legal (the model's own docstring calls this out:
      // a damaged/illegible meter may have no readable serial), so only "Ép" gets downgraded
      // here - never "Sérült", which must never be silently discarded (installer-entered
      // field data). okRef is an uncontrolled <select> synced only via defaultValue, so the
      // DOM value must be corrected as well - otherwise the *next* blur (of any field, since
      // meterIdCleared is recomputed fresh from the still-"---" ref every time) re-reads the
      // still-"Ép" native select value on line above and silently overwrites this null back
      // to true, reintroducing the same constraint violation on upload.
      actInvItemListItem.meter_ok = null;
      if (okRef?.current) {
        okRef.current.value = "Hiányzik";
      }
      // Only reached on the actual "Ép" -> "Hiányzik" transition (the guard above stops
      // re-firing once okRef.current.value is no longer "true"), so this cannot repeat on
      // every blur while the field stays cleared.
      toast.warning("A gyáriszám törlésével a vízmérő állapota 'Hiányzik'-ra állt vissza.");
    }
    // Same plain-text-vs-Select distinction, and same durable-ref reasoning, as
    // meterIdCleared above.
    if (!isNewWaterMeter) {
      updatePlainTextClearedTracking(seal1IdLastRawValueRef, seal1IdClearedRef, p1idRef?.current?.value ?? "");
    }
    const seal1IdCleared = isNewWaterMeter
      ? p1idRef?.current?.value == "---"
      : seal1IdClearedRef.current;
    const prevPlomba1Identifier = actInvItemListItem.plomba1_identifier;
    if (!!p1idRef?.current?.value || seal1IdCleared) {
      actInvItemListItem.plomba1_identifier = seal1IdCleared ? null : p1idRef.current.value;
    }
    actInvItemListItem.plomba1_ok = p1okRef.current.value;
    if (shouldAutoSetOkOnIdentifierEntry(prevPlomba1Identifier, actInvItemListItem.plomba1_identifier, p1okRef?.current?.value)) {
      actInvItemListItem.plomba1_ok = "true";
      p1okRef.current.value = "true";
      toast.info("A záróelem-1 megadásával az 1. plomba állapota 'Ép'-re állt.");
    }
    if (seal1IdCleared && p1okRef?.current?.value == "true") {
      // Same DB constraint (wm_plomba1_id_or_plomba1_not_ok), same "only downgrade Ép, never
      // Sérült" reasoning, and same DOM-select correction as meter_ok above.
      actInvItemListItem.plomba1_ok = null;
      if (p1okRef?.current) {
        p1okRef.current.value = "Hiányzik";
      }
      // Same "only on the actual transition" reasoning as meter_ok above.
      toast.warning("A záróelem-1 törlésével az 1. plomba állapota 'Hiányzik'-ra állt vissza.");
    }
    // Same plain-text-vs-Select distinction, and same durable-ref reasoning, as
    // meterIdCleared above.
    if (!isNewWaterMeter) {
      updatePlainTextClearedTracking(seal2IdLastRawValueRef, seal2IdClearedRef, p2idRef?.current?.value ?? "");
    }
    const seal2IdCleared = isNewWaterMeter
      ? p2idRef?.current?.value == "---"
      : seal2IdClearedRef.current;
    const prevPlomba2Identifier = actInvItemListItem.plomba2_identifier;
    if (!!p2idRef?.current?.value || seal2IdCleared) {
      actInvItemListItem.plomba2_identifier = seal2IdCleared ? null : p2idRef.current.value;
    }
    actInvItemListItem.plomba2_ok = p2okRef.current.value;
    // No DB constraint ties plomba2_ok to plomba2_identifier, but the installer-facing
    // behavior is kept identical to plomba1: Ép -> Hiányzik on clear, Hiányzik -> Ép on entry.
    if (seal2IdCleared && p2okRef?.current?.value == "true") {
      actInvItemListItem.plomba2_ok = null;
      p2okRef.current.value = "Hiányzik";
      toast.warning("A záróelem-2 törlésével a 2. plomba állapota 'Hiányzik'-ra állt vissza.");
    }
    if (shouldAutoSetOkOnIdentifierEntry(prevPlomba2Identifier, actInvItemListItem.plomba2_identifier, p2okRef?.current?.value)) {
      actInvItemListItem.plomba2_ok = "true";
      p2okRef.current.value = "true";
      toast.info("A záróelem-2 megadásával a 2. plomba állapota 'Ép'-re állt.");
    }
    actInvItemListItem.note = noteRef.current.value;

    propState.SetWmWorkList(tempInvItem);
  };
  useEffect(() => {
    if (!!barCodeRes) {
      const barcode = formatSealWatermeterId(barCodeRes);
      let tempInvItem = Object.assign({}, propState.wmWorkList);

      switch (barcodeScannerOn) {
        case idRef:
          tempInvItem[`${propState.actTask}`].wmlist[
            propState.item
          ].meter_identifier = barcode;
          if (idRef?.current) idRef.current.value = barcode;
          break;
        case p1idRef:
          tempInvItem[`${propState.actTask}`].wmlist[
            propState.item
          ].plomba1_identifier = barcode;
          if (p1idRef?.current) p1idRef.current.value = barcode;
          break;
        case p2idRef:
          tempInvItem[`${propState.actTask}`].wmlist[
            propState.item
          ].plomba2_identifier = barcode;
          if (p2idRef?.current) p2idRef.current.value = barcode;
          break;

        default:
          break;
      }
      propState.SetWmWorkList(tempInvItem);
      setBarcodeScannerOn(null);
      setBarCodeRes(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [barCodeRes]);

  return (
    <>
      <ReactModal
        key={propState.keyProp + "barcmod"}
        isOpen={!!barcodeScannerOn}
        appElement={document.getElementById("root")}
        style={{
          width: "100%",
          height: "100%",
          margin: "0",
          padding: "0",
          dialog: {
            width: "100%",
            height: "100%",
            margin: "0",
            padding: "0",
          },
          content: {
            width: "100%",
            height: "auto",
            minHeight: "100%",
            inset: "0",
            borderRadius: "0",
            margin: "0",
            padding: "0",
          },
        }}
        contentLabel="Barcode Modal"
      >
        <BarcodeScanner
          setBarCodeRes={(bcRs) => {
            setBarCodeRes(bcRs); /*  wmBcode.exit();  */
          }}
          key={propState.keyProp + "barcrdr"}
        />
      </ReactModal>
      <Form key={propState.keyProp + "wmform"}>
        <Form.Group controlId="phys">
          <Form.Label>
            <small style={{ color: "indigo", textAlign: "left" }}>
              Készülékhely/felhasználási hely:
            </small>
          </Form.Label>
          <Form.Control
            disabled={isButtonDisabled}
            placeholder="Vízmérő helye"
            defaultValue={localPhysicalPlace}
            onBlur={(e) => { onInputChange(e); propState.setDefaultPhysicalPlaces(e.target.value);}}
            name="note"
            ref={placeRef}
          />
        </Form.Group>
        <Form.Group controlId="wm_id">
          <Form.Label key={propState.keyProp + "focus"}
              ref={(focref) => {
                if (!!focref)
                  propState.focusRefList.current[propState.keyProp + "focus"] =
                    focref;
              }}>
            <small style={{ color: "indigo", textAlign: "left" }}>
              Gyáriszám:
            </small>
          </Form.Label>
          <Form.Row className="align-items-center ">
            <Col className="px-0">
              {isNewWaterMeter ? (
                <LookUpFormField
                  placeholder="Gyáriszám"
                  onBlur={(e) => { updateWaterMeterData(idRef.current.value); onInputChange(e); }}
                  defaultValue={propStateListWmItem.current.meter_identifier}
                  name="wmID"
                  parentRef={idRef}
                  optDataList={propState.wmList}
                  wmItem={propState.item}
                  wmListOfTask={propState.wmWorkList[`${props.actTask}`].wmlist}
                  taskId={props.actTask}
                  lookupFieldReload={lookupFieldReload}
                  setLookupFieldReload={setLookupFieldReload}
                  disabled={!!propStateListWmItem.current.id || isButtonDisabled}
                />
              ) : (
                <Form.Control
                  disabled={!!propStateListWmItem.current.id || isButtonDisabled}
                  type="text"
                  placeholder="Gyáriszám"
                  onBlur={onInputChange}
                  defaultValue={propStateListWmItem.current.meter_identifier}
                  name="wmID"
                  ref={idRef}
                />
              )}
            </Col>
            <Col xs="auto">
              <Button
                disabled={!!propStateListWmItem.current.id || isButtonDisabled}
                className="px-1 mx-0 py-0 "
                variant="outline-dark"
                onClick={() => {
                  /* wmBcode.enter();  */ setBarcodeScannerOn(idRef);
                }}
              >
                <h5>
                  <FaQrcode />
                </h5>
              </Button>
            </Col>
          </Form.Row>
        </Form.Group>

        <Form.Group controlId="cold_hot" classname="container">
          <div class="row">
            <div class="col">          
              <Form.Label>
                <small style={{ color: "indigo", textAlign: "left" }}>
                  Hideg/meleg:
                </small>
              </Form.Label>
              <Form.Control
                disabled={isButtonDisabled}
                onBlur={onInputChange}
                as="select"
                custom
                ref={cold_hotRef}
                defaultValue={propStateListWmItem.current.is_cold}
              >
                <option value={null}>Válassz...</option>
                <option value={false}>Meleg</option>
                <option value={true}>Hideg</option>
              </Form.Control>{" "}
            </div>

            <div class="col">          
              <Form.Label>
                <small style={{ color: "indigo", textAlign: "left" }}>
                  Jeladós:
                </small>
              </Form.Label>
              <Form.Control
                disabled={isButtonDisabled}
                onBlur={onInputChange}
                as="select"
                custom
                ref={transmitterRef}
                defaultValue={propStateListWmItem.current.meter_is_transmitter_supported}
              >
                <option value={null}>Válassz...</option>
                <option value={false}>Nem</option>
                <option value={true}>Igen</option>
              </Form.Control>{" "}
            </div>
          </div>
        </Form.Group>
        <Form.Group controlId="size" classname="container">
          <div class="row">
            <div class="col">
              <Form.Label>
                <small style={{ color: "indigo", textAlign: "left" }}>
                  Vízmérő átmérő:
                </small>
              </Form.Label>

              <Form.Control
                disabled={isButtonDisabled}
                onBlur={onInputChange}
                as="select"
                custom
                ref={sizeRef}
                defaultValue={propStateListWmItem.current.size}
              >
                <option value={null}>Válassz...</option>
                <option value={13}>13</option>
                <option value={15}>15</option>
                <option value={20}>20</option>
                <option value={25}>25</option>
                <option value={32}>32</option>
              </Form.Control>{" "}
            </div>
            <div class="col">
              <Form.Label>
                <small style={{ color: "indigo", textAlign: "left" }}>
                  Beépítési hossz:
                </small>
              </Form.Label>
              <Form.Control
                disabled={isButtonDisabled}
                onBlur={onInputChange}
                as="select"
                custom
                ref={lengthRef}
                defaultValue={propStateListWmItem.current.meter_length_in_mm}
              >
                <option value={null}>Válassz...</option>
                <option value={80}>80</option>
                <option value={105}>105</option>
                <option value={110}>110</option>
                <option value={130}>130</option>
                <option value={165}>165</option>
                <option value={170}>170</option>
                <option value={190}>190</option>
                <option value={260}>260</option>
                <option value={300}>300</option>
              </Form.Control>{" "}
            </div>
          </div>    
        </Form.Group>
        <Form.Group controlId="meterok" classname="container">
          <div class="row">
            <div class="col">
              <Form.Label>
                <small style={{ color: "indigo", textAlign: "left" }}>
                  {" "}
                  Vízmérő állapota:
                </small>
              </Form.Label>
              <Form.Control
                disabled={isButtonDisabled}
                onBlur={onInputChange}
                as="select"
                className="my-1 mr-sm-2"
                custom
                ref={okRef}
                defaultValue={propStateListWmItem.current.meter_ok}
              >
                <option value={null}>Hiányzik</option>
                <option value={true}>Ép</option>
                <option value={false}>Sérült</option>
              </Form.Control>{" "}
            </div>
            <div class="col">
              <Form.Label>
                <small style={{ color: "indigo", textAlign: "left" }}>
                  {" "}
                  Modell:
                </small>
              </Form.Label>
              <Form.Control
                disabled={isButtonDisabled}
                type="text"
                placeholder="MOM"
                onBlur={onInputChange}
                defaultValue={propStateListWmItem.current.meter_model}
                name="model"
                ref={modelRef}
              />{" "}
            </div>    
          </div>    
        </Form.Group>
        <Form.Group controlId="meter" classname="container">
          <div class="row">
            <div class="col">
              <Form.Label>
                <small style={{ color: "indigo", textAlign: "left" }}>
                  Mérőállás:
                </small>
              </Form.Label>
              <Form.Control
                disabled={isButtonDisabled}
                type="number"
                placeholder="Mérő állása"
                onBlur={onInputChange}
                defaultValue={propStateListWmItem.current.act_meter}
                name="meter"
                ref={meterRef}
              />{" "}
            </div>    
            <div class="col"> 
              <Form.Label>
                <small style={{ color: "indigo", textAlign: "left" }}>
                  {" "}
                  Hitelesítés:
                </small>
              </Form.Label>
              <Form.Control
                disabled={isButtonDisabled}
                as="select"
                placeholder="MOM"
                onBlur={onInputChange}
                name="model"
                custom
                ref={meterValidFromRef}
                defaultValue={propStateListWmItem.current.meter_valid_from}
              >
                <option value={null}>Válassz...</option>
                <option value={new Date().getFullYear()-12}>{new Date().getFullYear()-12}</option>
                <option value={new Date().getFullYear()-11}>{new Date().getFullYear()-11}</option>
                <option value={new Date().getFullYear()-10}>{new Date().getFullYear()-10}</option>
                <option value={new Date().getFullYear()-9}>{new Date().getFullYear()-9}</option>
                <option value={new Date().getFullYear()-8}>{new Date().getFullYear()-8}</option>
                <option value={new Date().getFullYear()-7}>{new Date().getFullYear()-7}</option>
                <option value={new Date().getFullYear()-6}>{new Date().getFullYear()-6}</option>
                <option value={new Date().getFullYear()-5}>{new Date().getFullYear()-5}</option>
                <option value={new Date().getFullYear()-4}>{new Date().getFullYear()-4}</option>
                <option value={new Date().getFullYear()-3}>{new Date().getFullYear()-3}</option>
                <option value={new Date().getFullYear()-2}>{new Date().getFullYear()-2}</option>
                <option value={new Date().getFullYear()-1}>{new Date().getFullYear()-1}</option>
                <option value={new Date().getFullYear()}>{new Date().getFullYear()}</option>
                <option value={new Date().getFullYear()+1}>{new Date().getFullYear()+1}</option> 
              </Form.Control>{" "}
            </div>
          </div>    
        </Form.Group>

        <Form.Group controlId="p1_id">
          <Form.Label>
            <small style={{ color: "indigo", textAlign: "left" }}>
              Biztonsági záróelem-1 száma:
            </small>
          </Form.Label>
          <Form.Row className="align-items-center">
            <Col className="px-0">
              {isNewWaterMeter ? (
                <LookUpFormField
                  placeholder="Biztonsági záróelem-1 száma"
                  onBlur={onInputChange}
                  defaultValue={propStateListWmItem.current.plomba1_identifier}
                  name="seal1ID"
                  parentRef={p1idRef}
                  optDataList={propState.sealList}
                  wmItem={propState.item}
                  wmListOfTask={propState.wmWorkList[`${props.actTask}`].wmlist}
                  taskId={props.actTask}
                  lookupFieldReload={lookupFieldReload}
                  setLookupFieldReload={setLookupFieldReload}
                  disabled={!!propStateListWmItem.current.id || isButtonDisabled}
                />
              ) : (
                <Form.Control
                  disabled={!!propStateListWmItem.current.id || isButtonDisabled}
                  type="text"
                  placeholder="Biztonsági záróelem-1 száma"
                  onBlur={onInputChange}
                  defaultValue={propStateListWmItem.current.plomba1_identifier}
                  name="seal1ID"
                  ref={p1idRef}
                />
              )}
            </Col>
            <Col xs="auto">
              <Button
                disabled={!!propStateListWmItem.current.id || isButtonDisabled}
                className="px-1 mx-0 py-0 "
                variant="outline-dark"
                onClick={() => {
                  /*  wmBcode.enter();  */ setBarcodeScannerOn(p1idRef);
                }}
              >
                <h5>
                  <FaQrcode />
                </h5>
              </Button>
            </Col>
          </Form.Row>
        </Form.Group>
        <Form.Group controlId="p1ok">
          <Form.Label className="my-1 mr-2">
            <small style={{ color: "indigo", textAlign: "left" }}>
              Záróelem-1 állapota:
            </small>
          </Form.Label>
          <Form.Control
            disabled={isButtonDisabled}
            onBlur={onInputChange}
            as="select"
            className="my-1 mr-sm-2"
            custom
            ref={p1okRef}
            defaultValue={propStateListWmItem.current.plomba1_ok}
          >
            <option value={null}>Hiányzik</option>
            <option value={true}>Ép</option>
            <option value={false}>Sérült</option>
          </Form.Control>{" "}
        </Form.Group>
        <Form.Group controlId="p2_id">
          <Form.Label>
            <small style={{ color: "indigo", textAlign: "left" }}>
              Biztonsági záróelem-2 száma:
            </small>
          </Form.Label>
          <Form.Row className="align-items-center">
            <Col className="px-0">
              {isNewWaterMeter ? (
                <LookUpFormField
                  placeholder="Biztonsági záróelem-2 száma"
                  onBlur={onInputChange}
                  defaultValue={propStateListWmItem.current.plomba2_identifier}
                  name="seal2ID"
                  parentRef={p2idRef}
                  optDataList={propState.sealList}
                  wmItem={propState.item}
                  wmListOfTask={propState.wmWorkList[`${props.actTask}`].wmlist}
                  taskId={props.actTask}
                  lookupFieldReload={lookupFieldReload}
                  setLookupFieldReload={setLookupFieldReload}
                  disabled={!!propStateListWmItem.current.id || isButtonDisabled}
                />
              ) : (
                <Form.Control
                  disabled={!!propStateListWmItem.current.id || isButtonDisabled}
                  type="text"
                  placeholder="Biztonsági záróelem-2 száma"
                  onBlur={onInputChange}
                  defaultValue={propStateListWmItem.current.plomba2_identifier}
                  name="seal2ID"
                  ref={p2idRef}
                />
              )}
            </Col>
            <Col xs="auto">
              <Button
                disabled={!!propStateListWmItem.current.id || isButtonDisabled}
                className="px-1 mx-0 py-0 "
                variant="outline-dark"
                onClick={() => {
                  /* wmBcode.enter(); */ setBarcodeScannerOn(p2idRef);
                }}
              >
                <h5>
                  <FaQrcode />
                </h5>
              </Button>
            </Col>
          </Form.Row>
        </Form.Group>
        <Form.Group controlId="p2ok">
          <Form.Label className="my-1 mr-2">
            <small style={{ color: "indigo", textAlign: "left" }}>
              Záróelem-2 állapota:
            </small>
          </Form.Label>
          <Form.Control
            disabled={isButtonDisabled}
            onBlur={onInputChange}
            as="select"
            className="my-1 mr-sm-2"
            custom
            ref={p2okRef}
            defaultValue={propStateListWmItem.current.plomba2_ok}
          >
            <option value={null}>Hiányzik</option>
            <option value={true}>Ép</option>
            <option value={false}>Sérült</option>
          </Form.Control>{" "}
        </Form.Group>
        <Form.Group controlId="note">
          <Form.Label>
            <small style={{ color: "indigo", textAlign: "left" }}>
            Szerelő megjegyzése az ügyfél felé:
            </small>
          </Form.Label>
          <Form.Control
            disabled={isButtonDisabled}
            as="textarea" rows="3"
            placeholder="Szerelő megjegyzése az ügyfél felé"
            onBlur={onInputChange}
            defaultValue={propStateListWmItem.current.note}
            name="note"
            ref={noteRef}
          />
        </Form.Group>
      </Form>
    </>
  );
};
export default WmeterForm;
